// Type definitions for Form Audit Alih Media RM Kertas → RME
// Reference: rme_retensi_alih_media_template.md v1.0.0 Section 1–10
// Compliance: PMK 24/2022 + MRMIK 5/11/12 + KMK 1423/2022 + UU PDP + UU ITE + FHIR R4 IHS

export type KelasRS = "A" | "B" | "C" | "D";
export type JenjangPmik = "d3_rmik" | "d4_mik" | "s1_mik" | "s2";

export type SessionStatus =
  | "draft"
  | "checklist_done"
  | "signed_pmik"
  | "signed_auditor"
  | "signed_karu_rm"
  | "signed_direktur"
  | "finalized"
  | "locked";

export type KlasifikasiHasil =
  | "sangat_baik"
  | "baik"
  | "cukup"
  | "kurang"
  | null;

export type Penilaian = "ya" | "tidak" | "na" | null;

export type PenandaWajib =
  | "PMK24"
  | "MRMIK"
  | "KMK1423"
  | "SATUSEHAT"
  | "UUPDP"
  | "UUITE"
  | "BP";

export type NilaiGuna = "permanent" | "transient" | "legal_hold";

export type KlasifikasiNilaiGuna = "permanen" | "musnah_25thn" | "campuran";

export type DocStatus =
  | "preliminary"
  | "final"
  | "superseded"
  | "entered_in_error";

export type SignerRole =
  | "pmik_pelaksana"
  | "auditor"
  | "karu_rm"
  | "direktur"
  | "saksi_pemusnah_1"
  | "saksi_pemusnah_2"
  | "saksi_pemusnah_3";

export type PsreProvider =
  | "internal_png"
  | "bsre"
  | "privyid"
  | "vida"
  | "peruri";

export type BeritaAcaraJenis = "alih_media" | "pemusnahan";

export interface SectionRekap {
  ya: number;
  tidak: number;
  na: number;
  total: number;
  persen: number | null;
}

export interface RetensiSession {
  id?: number;
  no_session?: string;
  no_sk_penugasan?: string | null;
  tanggal_audit: string;
  periode_audit_dari: string;
  periode_audit_sd: string;

  // Section 1
  rs_name_snapshot: string;
  kode_kemkes_rs?: string | null;
  kelas_rs?: KelasRS | null;
  alamat_rs?: Record<string, any> | null;
  nama_direktur: string;
  nama_unit_rme: string;

  // Section 2
  pmik_pelaksana_id: number;
  pmik_pelaksana_nama: string;
  pmik_nip_nrp?: string | null;
  pmik_str_no: string;
  pmik_str_exp: string;
  pmik_sik_no: string;
  pmik_sik_exp: string;
  pmik_jenjang_pendidikan?: JenjangPmik | null;
  pmik_jabatan?: string | null;
  pmik_pormiki_no?: string | null;

  // Section 3
  auditor_id: number;
  auditor_nama: string;
  auditor_str_no: string;
  auditor_jabatan?: string | null;

  karu_rm_nama?: string | null;

  // Rekap (read-only)
  rekap_per_section?: Record<string, SectionRekap> | null;
  total_ya?: number;
  total_tidak?: number;
  total_na?: number;
  total_item?: number;
  persen_kepatuhan_total?: number | null;
  klasifikasi_hasil?: KlasifikasiHasil;
  jumlah_temuan_kritis?: number;

  catatan_umum_auditor?: string | null;

  status?: SessionStatus;
  checklist_done_at?: string | null;
  finalized_at?: string | null;
  locked_at?: string | null;

  retention_until?: string | null;

  created_at?: string;
  updated_at?: string;

  // Relations (loaded on demand)
  batches?: RetensiBatch[];
  checklist_items?: RetensiChecklistItem[];
  signatures?: RetensiSignature[];
  berita_acara?: RetensiBeritaAcara[];
}

export interface RetensiBatch {
  id?: number;
  session_id?: number;
  batch_id_string?: string;
  tgl_mulai_alih_media: string;
  tgl_selesai_alih_media?: string | null;
  kriteria_inaktif: string;
  kunjungan_tahun_terlama: number;
  kunjungan_tahun_terbaru: number;
  jumlah_berkas: number;
  jumlah_nrm_unik: number;
  lokasi_fisik_asal: string;
  lokasi_rme_tujuan: string;
  klasifikasi_nilai_guna: KlasifikasiNilaiGuna;
  dokumen?: RetensiDokumen[];
}

export interface RetensiDokumen {
  id?: number;
  batch_id?: number;
  no_rm: string;
  nama_pasien_snapshot: string;
  tgl_lahir?: string | null;
  tgl_kunjungan_terakhir?: string | null;
  diagnosis_icd10?: string | null;
  jenis_dokumen_loinc?: string | null;
  jenis_dokumen_label?: string | null;
  kategori_nilai_guna: NilaiGuna;
  alasan_legal_hold?: string | null;
  tracking_id?: string;
  format_file?: "pdf_a_2" | "pdf_a_3" | "pdf" | "tiff" | "lain";
  file_size_bytes?: number | null;
  sha256_hash?: string | null;
  scan_resolution_dpi?: number | null;
  halaman_total?: number | null;
  halaman_terverifikasi?: number;
  watermark_applied?: boolean;
  dpjp_pencipta_id?: number | null;
  dpjp_pencipta_nama?: string | null;
  pmik_verifikator_id?: number | null;
  pmik_verifikator_nama?: string | null;
  verified_at?: string | null;
  doc_status?: DocStatus;
  kondisi_rusak_fisik?: boolean;
  catatan_kondisi?: string | null;
}

export interface RetensiBerkas {
  id?: number;
  batch_id?: number;
  register_no: number | null;
  no_rm: string;
  nama: string;
  jenis_kelamin: "L" | "P" | null;
  umur: number | null;
  alamat: string | null;
  diagnosa: string | null;
  tgl_masuk: string | null;
  tgl_keluar: string | null;
  unit_nama: string | null;
  keterangan: string | null;
  sudah_terdaftar?: boolean;
}

export interface RetensiChecklistItem {
  id?: number;
  session_id?: number;
  kode_item: string;
  kelompok: string;
  kelompok_label: string;
  aspek_penilaian: string;
  penanda_wajib: PenandaWajib[];
  penilaian: Penilaian;
  bukti_text?: string | null;
  catatan_auditor?: string | null;
  rekomendasi_tindak_lanjut?: string | null;
  penanggung_jawab_text?: string | null;
  tenggat_waktu?: string | null;
  is_temuan_kritis?: boolean;
  root_cause?: string | null;
  urutan_tampil?: number;
}

export interface RetensiSignature {
  id?: number;
  session_id?: number;
  role: SignerRole;
  signer_user_id?: number;
  signer_nama: string;
  signer_jabatan?: string | null;
  signer_str_no?: string | null;
  signature_format?: "png" | "cades" | "pades" | "xades";
  psre_provider?: PsreProvider;
  psre_serial_number?: string | null;
  signature_hash_sha256?: string | null;
  signed_at?: string;
}

export interface RetensiBeritaAcara {
  id?: number;
  session_id?: number;
  jenis: BeritaAcaraJenis;
  no_ba?: string;
  tgl_ba: string;
  jumlah_berkas?: number;
  jumlah_nrm?: number;
  ringkasan?: Record<string, any> | null;
  metode_pemusnahan?: string | null;
  pdf_path?: string | null;
  pdf_sha256?: string | null;
  pdf_size_bytes?: number | null;
  signed_count?: number;
  finalized?: boolean;
  finalized_at?: string | null;
}

export interface ScanKandidatParams {
  cutoff_date: string;
  tahun_terlama?: number;
  tahun_terbaru?: number;
  limit?: number;
  per_page?: number;
  page?: number;
}

export interface ScanKandidatRow {
  no_rm: string;
  nama_pasien: string | null;
  tgl_lahir: string | null;
  tgl_kunjungan_terakhir: string;
}

export interface ScanKandidatResponse {
  data: ScanKandidatRow[];
  meta: {
    cutoff_date: string;
    total_approx: number;
    per_page: number;
    current_page: number;
    limit: number;
  };
}

export interface SignPayload {
  role: SignerRole;
  password: string;
  signer_user_id: number;
  signer_nama: string;
  signer_jabatan?: string;
  signer_str_no?: string;
  signature_blob?: string;
  signature_format?: "png" | "cades" | "pades" | "xades";
  psre_provider?: PsreProvider;
  psre_serial_number?: string;
}

// UI helpers — label peringkat klasifikasi (Section 7.2)
export const KLASIFIKASI_LABELS: Record<Exclude<KlasifikasiHasil, null>, string> = {
  sangat_baik: "Sangat Baik (≥90%)",
  baik: "Baik (75–89%)",
  cukup: "Cukup (60–74%)",
  kurang: "Kurang (<60%)",
};

export const STATUS_LABELS: Record<SessionStatus, string> = {
  draft: "Draft",
  checklist_done: "Checklist Selesai",
  signed_pmik: "TTD PMIK",
  signed_auditor: "TTD Auditor",
  signed_karu_rm: "TTD Ka. Unit RM",
  signed_direktur: "TTD Direktur",
  finalized: "Final",
  locked: "Terkunci",
};

export const SIGNER_LABELS: Record<SignerRole, string> = {
  pmik_pelaksana: "PMIK Pelaksana",
  auditor: "Auditor",
  karu_rm: "Kepala Unit RM",
  direktur: "Direktur",
  saksi_pemusnah_1: "Saksi Pemusnah 1",
  saksi_pemusnah_2: "Saksi Pemusnah 2",
  saksi_pemusnah_3: "Saksi Pemusnah 3",
};
